import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import useFirestore from "../../../../config/fireBaseHook"; // Updated path and hook name
import { useAuth } from "../../../../config/AuthContext";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

// Blueprint shell_name formatting change takes effect for shells created on
// or after this date (CONVERT(DATETIME, '20260806', 112) in the formula).
const BLUEPRINT_CUTOFF = new Date(2026, 7, 6);

// Tartan ID is always 7 digits — pad with leading zeros if shorter.
const padTartanId = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return trimmed;
  return trimmed.padStart(7, "0");
};

const Add = ({ onRecordAdded }) => {
  const { addData } = useFirestore("TpCourseShell"); // Fixed method name

  // Hooks for fetching dropdown data
  const { data: creditCaptureStatusData } = useFirestore("CreditCaptureStatus");
  const { data: fpsData } = useFirestore("Fps");
  const { data: creditTypeData } = useFirestore("TpCreditType");
  const { data: courseData } = useFirestore("TpCourseCatalog");
  const { data: courseShellStatusData } = useFirestore("CourseShellStatus");
  const { data: shellRequestStatusData } = useFirestore("ShellRequestStatus");
  const { data: sitesData } = useFirestore("Sites");
  const { data: partnersData } = useFirestore("TpPartners");
  const { data: usersData } = useFirestore("users");
  const { data: elearnShellTypeData } = useFirestore("ElearnShellType");
  const { data: existingCourseShells } = useFirestore("TpCourseShell"); // For generating next TP section number
  const { data: pathwayData } = useFirestore("PathwaysPrograms");
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Get current school year for defaults
  const getCurrentSchoolYear = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-based, so 0 = January

    // If it's before August (month 7), we're in the second half of the school year
    if (currentMonth < 7) {
      return `${currentYear - 1}-${String(currentYear).slice(-2)}`;
    } else {
      return `${currentYear}-${String(currentYear + 1).slice(-2)}`;
    }
  };

  // Generate next TP Section Number
  const getNextTpSectionNumber = () => {
    if (!existingCourseShells || !Array.isArray(existingCourseShells)) {
      return "TP.A00001"; // Default if no existing data
    }

    // Filter and extract TP.A numbers
    const tpNumbers = existingCourseShells
      .filter(
        (shell) =>
          shell.tp_section_number && shell.tp_section_number.startsWith("TP.A"),
      )
      .map((shell) => {
        const match = shell.tp_section_number.match(/^TP\.A(\d+)$/);
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter((num) => !isNaN(num) && num > 0);

    // Find the highest number and add 1
    const maxNumber = tpNumbers.length > 0 ? Math.max(...tpNumbers) : 0;
    const nextNumber = maxNumber + 1;

    // Format with leading zeros (5 digits)
    return `TP.A${nextNumber.toString().padStart(5, "0")}`;
  };

  const [formData, setFormData] = useState({
    shell_name: "",
    tp_final_approval: 0,
    tp_final_authorization_name: "",
    tp_site_of_delivery: "",
    tp_partner: "",
    shell_school_year_start: getCurrentSchoolYear(),
    shell_school_year_end: getCurrentSchoolYear(),
    shell_type: "",
    tp_term: "",
    course: "",
    subject: "",
    credit_type: "",
    tp_section_number: "",
    primary_tp_inst_ID: "",
    secondary_tp_inst_ID: "",
    primary_fps_ID: "",
    secondary_fps_ID: "",
    tp_pathway_manager_ID: "",
    tp_pathway_program_ID: "",
    note_01: "",
    tp_partner_irn: "",
    sod_irn: "",
    status: "",
    what_year_taking_course: "",
    tech_prep_modality: "A",
    credit_capture_scheme: "",
    course_request_from: "",
    credentials_uploaded: "",
    credit_capture_status_note: "",
    credit_capture_status: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => {
      const newData = {
        ...prevData,
        [name]: value || "", // Ensure empty string instead of undefined
      };

      // Auto-calculate shell_name based on the formula for relevant fields
      if (
        [
          "shell_type",
          "course",
          "subject",
          "tp_section_number",
          "shell_school_year_start",
          "tp_site_of_delivery",
          "credit_type",
        ].includes(name)
      ) {
        newData.shell_name = calculateShellName(newData);
      }

      return newData;
    });
  };

  // Auto-generate TP Section Number when existing course shells data is loaded
  useEffect(() => {
    if (existingCourseShells && existingCourseShells.length >= 0) {
      const nextTpSectionNumber = getNextTpSectionNumber();
      setFormData((prevData) => {
        const newData = {
          ...prevData,
          tp_section_number: nextTpSectionNumber,
        };
        // Calculate shell_name with the new section number
        newData.shell_name = calculateShellName(newData);
        return newData;
      });
    }
  }, [existingCourseShells]);

  // Blueprint course shells created on/after 2026-08-06 use the new "BP - "
  // naming (CONVERT(DATETIME, '20260806', 112) in the source formula). A new
  // record has no time_created yet, so treat "now" as its creation date.
  const resolveTimeCreated = (data) => {
    const value = data.time_created;
    if (value) {
      if (typeof value.toDate === "function") return value.toDate(); // Firestore Timestamp
      if (value instanceof Date) return value;
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  };

  // Helper function to calculate shell_name
  const calculateShellName = (data) => {
    const {
      shell_type,
      course,
      subject,
      tp_section_number,
      shell_school_year_start,
      tp_site_of_delivery,
      credit_type,
    } = data;

    const courseStr = course || "";

    if (shell_type === "Master") {
      return `${courseStr} ${subject} (${tp_section_number}) - Tech Prep Master`;
    }

    if (
      shell_type === "Blueprint" &&
      resolveTimeCreated(data) >= BLUEPRINT_CUTOFF
    ) {
      const courseLeft3 = courseStr.substring(0, 3);
      const courseRight4 = courseStr.substring(courseStr.length - 4);
      return `BP - ${courseLeft3} ${courseRight4} ${subject} (${tp_section_number}) - TechPrep`;
    }

    if (courseStr === "CLT-1113 & CLT-1114") {
      return `${shell_school_year_start}.CLT.1113.&.CLT.1114.${tp_section_number} - ${subject} (${tp_site_of_delivery})`;
    }

    if (shell_type === "Regular" && credit_type === "ITAG") {
      const courseNoDots = courseStr.replace(/\./g, "");
      const digitIdx = courseNoDots.search(/[0-9]/);
      if (digitIdx > -1) {
        const prefix = courseNoDots.substring(0, digitIdx);
        const suffix = courseNoDots.substring(digitIdx);
        return `${shell_school_year_start}.${prefix}.${suffix}.${tp_section_number} - ${subject} (${tp_site_of_delivery})`;
      }
      return `${shell_school_year_start}.${courseNoDots}.${tp_section_number} - ${subject} (${tp_site_of_delivery})`;
    }

    const courseLeft3 = courseStr.substring(0, 3);
    const courseRight4 = courseStr.substring(courseStr.length - 4);
    return `${shell_school_year_start}.${courseLeft3}.${courseRight4}.${tp_section_number} - ${subject} (${tp_site_of_delivery})`;
  };

  // Handle course selection and auto-populate subject
  const handleCourseChange = (e) => {
    const { value } = e.target;
    const selectedCourse = courseData.find((course) => course.Course === value);

    setFormData((prevData) => {
      const newData = {
        ...prevData,
        course: value,
        subject: selectedCourse ? selectedCourse.Subject || "" : "",
      };

      // Calculate shell_name with new data
      newData.shell_name = calculateShellName(newData);

      return newData;
    });
  };

  // Handle TP Partner selection and auto-populate TP Partner IRN. Keyed by
  // PartnerIRN (stable) rather than Tech_Prep_Partner (freely renamable) so
  // a later rename in TpPartners can't disconnect this selection.
  const handleTpPartnerChange = (e) => {
    const { value } = e.target;
    const selectedPartner = partnersData?.find(
      (partner) => partner.PartnerIRN === value,
    );

    setFormData((prevData) => ({
      ...prevData,
      tp_partner: selectedPartner?.Tech_Prep_Partner || "",
      tp_partner_irn: value,
    }));
  };

  // Handle TP Site of Delivery selection and auto-populate SOD IRN. Keyed
  // by SiteID (stable) rather than SiteNameShort (freely renamable) so a
  // later rename in TpSites can't disconnect this selection.
  const handleTpSiteOfDeliveryChange = (e) => {
    const { value } = e.target;
    const selectedSite = sitesData?.find((site) => site.SiteID === value);

    setFormData((prevData) => {
      const newData = {
        ...prevData,
        tp_site_of_delivery: selectedSite?.SiteNameShort || "",
        sod_irn: value,
      };

      // Calculate shell_name with new data
      newData.shell_name = calculateShellName(newData);

      return newData;
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked ? -1 : 0,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Create a copy of formData to potentially modify
      const submitData = { ...formData };

      // Stamp the real creation time once, here, so later edits (which
      // never touch this field) have a stable value to compare against
      // BLUEPRINT_CUTOFF in calculateShellName instead of drifting to
      // "now" on every save.
      submitData.time_created = new Date();

      // If TP Final Approval is checked and user is logged in, set authorization name
      if (submitData.tp_final_approval === -1 && user?.name) {
        submitData.tp_final_authorization_name = user.name;
        submitData.time_approved = new Date();
      }

      await addData(submitData); // Fixed method name
      setLoading(false);
      setSnackbarOpen(true);
      if (onRecordAdded) {
        onRecordAdded(); // Call parent callback
      }
      setFormData({
        shell_name: "",
        tp_final_approval: 0,
        tp_final_authorization_name: "",
        tp_site_of_delivery: "",
        tp_partner: "",
        shell_school_year_start: getCurrentSchoolYear(),
        shell_school_year_end: getCurrentSchoolYear(),
        shell_type: "",
        tp_term: "",
        course: "",
        subject: "",
        credit_type: "",
        tp_section_number: getNextTpSectionNumber(),
        primary_tp_inst_ID: "",
        secondary_tp_inst_ID: "",
        primary_fps_ID: "",
        secondary_fps_ID: "",
        tp_pathway_manager_ID: "",
        tp_pathway_program_ID: "",
        note_01: "",
        tp_partner_irn: "",
        sod_irn: "",
        status: "",
        what_year_taking_course: "",
        tech_prep_modality: "A",
        credit_capture_scheme: "",
        course_request_from: "",
        credentials_uploaded: "",
        credit_capture_status_note: "",
        credit_capture_status: "",
      });
      setOpen(false);
    } catch (error) {
      console.error("Error adding record:", error);
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <div>
      <Button
        variant="contained"
        color="success"
        onClick={() => setOpen(true)}
        sx={{ marginBottom: "10px", color: "white" }}
      >
        Add Record
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <ModalDialogTitle onClose={() => setOpen(false)}>
          Add Record
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Shell Name"
                name="shell_name"
                value={formData.shell_name}
                disabled
                fullWidth
                margin="normal"
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#999",
                    backgroundColor: "#f5f5f5",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="TP Final Authorization Name"
                name="tp_final_authorization_name"
                value={formData.tp_final_authorization_name}
                disabled
                fullWidth
                margin="normal"
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#999",
                    backgroundColor: "#f5f5f5",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>TP Site of Delivery</InputLabel>
                <Select
                  name="tp_site_of_delivery"
                  value={formData.sod_irn || ""}
                  onChange={handleTpSiteOfDeliveryChange}
                  label="TP Site of Delivery"
                >
                  {sitesData
                    ?.sort((a, b) =>
                      (a.SiteNameShort || "").localeCompare(
                        b.SiteNameShort || "",
                      ),
                    )
                    .map((site) => (
                      <MenuItem key={site.id} value={site.SiteID}>
                        {site.SiteNameShort}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>TP Partner</InputLabel>
                <Select
                  name="tp_partner"
                  value={formData.tp_partner_irn || ""}
                  onChange={handleTpPartnerChange}
                  label="TP Partner"
                >
                  {partnersData
                    ?.filter((partner) => partner.Active_yn === "y")
                    .sort((a, b) =>
                      (a.Tech_Prep_Partner || "").localeCompare(
                        b.Tech_Prep_Partner || "",
                      ),
                    )
                    .map((partner) => (
                      <MenuItem key={partner.id} value={partner.PartnerIRN}>
                        {partner.Tech_Prep_Partner}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Shell School Year Start"
                name="shell_school_year_start"
                value={formData.shell_school_year_start}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Shell School Year End"
                name="shell_school_year_end"
                value={formData.shell_school_year_end}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Shell Type</InputLabel>
                <Select
                  name="shell_type"
                  value={formData.shell_type}
                  onChange={handleChange}
                  label="Shell Type"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {elearnShellTypeData
                    ?.sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                    .map((shellType) => (
                      <MenuItem key={shellType.id} value={shellType.name}>
                        {shellType.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>TP Term</InputLabel>
                <Select
                  name="tp_term"
                  value={formData.tp_term}
                  onChange={handleChange}
                  label="TP Term"
                >
                  <MenuItem value="All Year">All Year</MenuItem>
                  <MenuItem value="Fall">Fall</MenuItem>
                  <MenuItem value="Spring">Spring</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Course</InputLabel>
                <Select
                  name="course"
                  value={formData.course}
                  onChange={handleCourseChange}
                  label="Course"
                >
                  {courseData
                    ?.filter(
                      (course, index, self) =>
                        // Remove duplicates by keeping only the first occurrence of each Course value
                        index ===
                        self.findIndex((c) => c.Course === course.Course),
                    )
                    .sort((a, b) =>
                      (a.Course || "").localeCompare(b.Course || ""),
                    )
                    .map((course) => (
                      <MenuItem key={course.id} value={course.Course}>
                        {course.Course}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                fullWidth
                margin="normal"
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Credit Type</InputLabel>
                <Select
                  name="credit_type"
                  value={formData.credit_type}
                  onChange={handleChange}
                  label="Credit Type"
                >
                  {creditTypeData
                    .sort((a, b) =>
                      (a.Credit_Type_Name || "").localeCompare(
                        b.Credit_Type_Name || "",
                      ),
                    )
                    .map((type) => (
                      <MenuItem key={type.id} value={type.Credit_Type_abv}>
                        {type.Credit_Type_Name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="TP Section Number"
                name="tp_section_number"
                value={formData.tp_section_number}
                onChange={handleChange}
                fullWidth
                margin="normal"
                InputProps={{
                  readOnly: true,
                }}
                helperText="Auto-generated"
                sx={{
                  "& .MuiInputBase-input": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                    color: "rgba(0, 0, 0, 0.8)",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Primary TP Instructor</InputLabel>
                <Select
                  name="primary_tp_inst_ID"
                  value={formData.primary_tp_inst_ID}
                  onChange={handleChange}
                  label="Primary TP Instructor"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {usersData
                    ?.filter((user) => user.userTypes?.instructor === true)
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                    .map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Secondary TP Instructor</InputLabel>
                <Select
                  name="secondary_tp_inst_ID"
                  value={formData.secondary_tp_inst_ID}
                  onChange={handleChange}
                  label="Secondary TP Instructor"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {usersData
                    ?.filter((user) => user.userTypes?.instructor === true)
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                    .map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Primary FPS</InputLabel>
                <Select
                  name="primary_fps_ID"
                  value={formData.primary_fps_ID}
                  onChange={handleChange}
                  label="Primary FPS"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {fpsData
                    ?.filter((fps) => fps.Full_Name && fps.fps_Id)
                    .sort((a, b) =>
                      (a.Full_Name || "").localeCompare(b.Full_Name || ""),
                    )
                    .map((fps) => (
                      <MenuItem key={fps.id} value={fps.fps_Id}>
                        {fps.Full_Name}
                        {fps.Tartan_Id ? ` (${padTartanId(fps.Tartan_Id)})` : ""}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Secondary FPS</InputLabel>
                <Select
                  name="secondary_fps_ID"
                  value={formData.secondary_fps_ID}
                  onChange={handleChange}
                  label="Secondary FPS"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {fpsData
                    ?.filter((fps) => fps.Full_Name && fps.fps_Id)
                    .sort((a, b) =>
                      (a.Full_Name || "").localeCompare(b.Full_Name || ""),
                    )
                    .map((fps) => (
                      <MenuItem key={fps.id} value={fps.fps_Id}>
                        {fps.Full_Name}
                        {fps.Tartan_Id ? ` (${padTartanId(fps.Tartan_Id)})` : ""}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>TP Pathway Manager</InputLabel>
                <Select
                  name="tp_pathway_manager_ID"
                  value={formData.tp_pathway_manager_ID}
                  onChange={handleChange}
                  label="TP Pathway Manager"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {usersData
                    ?.filter((user) => user.userTypes?.pathwayManager === true)
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                    .map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Pathway Program</InputLabel>
                <Select
                  name="tp_pathway_program_ID"
                  value={formData.tp_pathway_program_ID || ""}
                  onChange={handleChange}
                  label="Pathway Program"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {pathwayData
                    ?.sort((a, b) =>
                      (a.PathwayName || "").localeCompare(b.PathwayName || ""),
                    )
                    .map((pathway) => (
                      <MenuItem key={pathway.id} value={pathway.id}>
                        {pathway.PathwayName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Note 01"
                name="note_01"
                value={formData.note_01}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="TP Partner IRN"
                name="tp_partner_irn"
                value={formData.tp_partner_irn}
                onChange={handleChange}
                fullWidth
                margin="normal"
                disabled
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "rgba(0, 0, 0, 0.6)",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="SOD IRN"
                name="sod_irn"
                value={formData.sod_irn}
                onChange={handleChange}
                fullWidth
                margin="normal"
                disabled
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "rgba(0, 0, 0, 0.6)",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Shell Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Shell Status"
                >
                  {shellRequestStatusData
                    ?.sort((a, b) =>
                      (a.Status || "").localeCompare(b.Status || ""),
                    )
                    .map((status) => (
                      <MenuItem key={status.id} value={status.Status}>
                        {status.Status}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>What Year Taking Course</InputLabel>
                <Select
                  name="what_year_taking_course"
                  value={formData.what_year_taking_course}
                  onChange={handleChange}
                  label="What Year Taking Course"
                >
                  <MenuItem value="Juniors">Juniors</MenuItem>
                  <MenuItem value="Juniors & Seniors">
                    Juniors & Seniors
                  </MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                  <MenuItem value="Seniors">Seniors</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Tech Prep Modality"
                name="tech_prep_modality"
                value={formData.tech_prep_modality}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Credit Capture Scheme</InputLabel>
                <Select
                  name="credit_capture_scheme"
                  value={formData.credit_capture_scheme}
                  onChange={handleChange}
                  label="Credit Capture Scheme"
                >
                  <MenuItem value="AA Scheme">AA Scheme</MenuItem>
                  <MenuItem value="FLEX Scheme">FLEX Scheme</MenuItem>
                  <MenuItem value="PR Scheme">PR Scheme</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Course Request From"
                name="course_request_from"
                value={formData.course_request_from}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Credentials Uploaded"
                name="credentials_uploaded"
                value={formData.credentials_uploaded}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Credit Capture Status</InputLabel>
                <Select
                  name="credit_capture_status"
                  value={formData.credit_capture_status}
                  onChange={handleChange}
                  label="Credit Capture Status"
                >
                  {creditCaptureStatusData
                    .sort((a, b) =>
                      (a.Credit_Capture_Status || "").localeCompare(
                        b.Credit_Capture_Status || "",
                      ),
                    )
                    .map((status) => (
                      <MenuItem
                        key={status.id}
                        value={status.Credit_Capture_Status}
                      >
                        {status.Credit_Capture_Status}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.tp_final_approval === -1}
                    onChange={handleCheckboxChange}
                    name="tp_final_approval"
                  />
                }
                label="TP Final Approval"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Credit Capture Status Note"
                name="credit_capture_status_note"
                value={formData.credit_capture_status_note}
                onChange={handleChange}
                fullWidth
                margin="normal"
                multiline
                rows={4}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions onSave={handleSubmit} saving={loading} />
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message="Record has been added"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        ContentProps={{
          sx: {
            backgroundColor: "black",
            color: "white",
          },
        }}
      />
    </div>
  );
};

export default Add;
